import { MultiSelect, Checkbox, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { SERVICE_TEMPLATES } from "@/types/templates";
import { IconFilterOff } from "@tabler/icons-react";
import { TagIcon } from "@/lib/tag-utils";

interface Option {
    value: string;
    label: string;
    disabled?: boolean;
}

interface Props {
    categoriesData: { category: string }[];
    selectedCategories: string[];
    setSelectedCategories: (categories: string[]) => void;

    attrKeys: string[];
    attrOptionsMap: Record<string, Option[]>;
    selectedAttrs: Record<string, string[]>;
    setSelectedAttrs: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;

    activeFiltersCount: number;
    onClearAll: () => void;
}

export default function TariffsFilters({
    categoriesData,
    selectedCategories,
    setSelectedCategories,
    attrKeys,
    attrOptionsMap,
    selectedAttrs,
    setSelectedAttrs,
    activeFiltersCount,
    onClearAll,
}: Props) {
    const { t } = useTranslation();

    const serviceCategoryKeys = useMemo(() => Object.keys(SERVICE_TEMPLATES), []);

    const catOptions = useMemo(() => {
        return (categoriesData || [])
            .filter(Boolean)
            .map(cat => ({ value: cat.category, label: t(cat.category) }))
            .sort((a, b) => {
                const idxA = serviceCategoryKeys.indexOf(a.value);
                const idxB = serviceCategoryKeys.indexOf(b.value);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.label.localeCompare(b.label);
            });
    }, [categoriesData, serviceCategoryKeys, t]);

    const formatLabel = (str: string) => {
        if (!str) return '';
        if (str === 'true' || str === 'yes') return t('yes');
        if (str === 'false' || str === 'no') return t('no');
        const translated = t(str);
        if (translated === str || translated === str.charAt(0).toUpperCase() + str.slice(1)) {
            return str
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
        return translated;
    };

    const handleCategoryClick = (categoryValue: string) => {
        if (selectedCategories.includes(categoryValue)) {
            setSelectedCategories([]);
            setSelectedAttrs({});
        } else {
            setSelectedCategories([categoryValue]);
            setSelectedAttrs({});
        }
    };

    const hasSelectedCategory = selectedCategories.length > 0;

    return (
        <div className="space-y-3 text-left">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-base text-gray-800">{t('filters')}</span>
                    {activeFiltersCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-gray-200 text-primary rounded-full">
                            {activeFiltersCount}
                        </span>
                    )}
                </div>
                <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    leftSection={<IconFilterOff size={14} />}
                    onClick={onClearAll}
                    disabled={activeFiltersCount === 0}
                    className="hover:bg-red-50"
                >
                    {t('clean_filters')}
                </Button>
            </div>

            {/* Category Badges / Pills */}
            <div>
                <div className="flex flex-wrap gap-1.5">
                    {catOptions.map(cat => {
                        const isSelected = selectedCategories.includes(cat.value);
                        return (
                            <div
                                key={cat.value}
                                onClick={() => handleCategoryClick(cat.value)}
                                className={`
                                    flex items-center gap-1 px-2.5 py-1 
                                    rounded-full 
                                    text-xs
                                    transition-all duration-150 
                                    cursor-pointer
                                    border 
                                    hover:shadow-sm
                                    ${isSelected
                                        ? 'border-gray-900 text-gray-900 font-bold bg-green-50'
                                        : 'bg-slate-50 text-slate-700 border-slate-200'
                                    }`}
                            >
                                <TagIcon name={cat.value} hideText size="xs" />
                                <span>{cat.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Attribute Filters (Revealed once ONE category is selected) */}
            {hasSelectedCategory && attrKeys.length > 0 && (
                <div className="pt-3">
                    <div className="max-h-[480px] overflow-y-auto pr-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {attrKeys.map(key => {
                                const rawOpts = attrOptionsMap[key] || [];
                                if (rawOpts.length === 0) return null;

                                const labelText = formatLabel(key);
                                const isBooleanAttr = rawOpts.every(opt => opt.value === 'true' || opt.value === 'false');

                                if (isBooleanAttr) {
                                    const selectedValues = selectedAttrs[key] || [];
                                    const isTrueChecked = selectedValues.includes('true');
                                    const isFalseChecked = selectedValues.includes('false');

                                    const trueOpt = rawOpts.find(o => o.value === 'true');
                                    const falseOpt = rawOpts.find(o => o.value === 'false');

                                    const isTrueDisabled = Boolean(trueOpt?.disabled) && !isTrueChecked;
                                    const isFalseDisabled = Boolean(falseOpt?.disabled) && !isFalseChecked;

                                    const handleToggle = (val: 'true' | 'false', checked: boolean) => {
                                        setSelectedAttrs(prev => {
                                            const current = prev[key] || [];
                                            const next = checked
                                                ? [...current.filter(v => v !== val), val]
                                                : current.filter(v => v !== val);
                                            return { ...prev, [key]: next };
                                        });
                                    };

                                    return (
                                        <div key={key} className="flex flex-col">
                                            <label className="text-xs font-medium text-gray-700 mb-1">
                                                {labelText}
                                            </label>
                                            <div className="flex items-center gap-4 py-1.5">
                                                <Checkbox
                                                    size="xs"
                                                    color="dark"
                                                    label={t('yes')}
                                                    checked={isTrueChecked}
                                                    disabled={isTrueDisabled}
                                                    onChange={(e) => handleToggle('true', e.currentTarget.checked)}
                                                />
                                                <Checkbox
                                                    size="xs"
                                                    color="dark"
                                                    label={t('no')}
                                                    checked={isFalseChecked}
                                                    disabled={isFalseDisabled}
                                                    onChange={(e) => handleToggle('false', e.currentTarget.checked)}
                                                />
                                            </div>
                                        </div>
                                    );
                                }

                                const opts = rawOpts.map(opt => ({
                                    value: opt.value,
                                    label: formatLabel(opt.value),
                                    disabled: opt.disabled
                                }));

                                return (
                                    <div key={key} className="flex flex-col">
                                        <label className="text-xs font-medium text-gray-700 mb-1">
                                            {labelText}
                                        </label>
                                        <MultiSelect
                                            size="sm"
                                            placeholder={labelText}
                                            data={opts}
                                            value={selectedAttrs[key] || []}
                                            onChange={(values) => {
                                                setSelectedAttrs(prev => ({
                                                    ...prev,
                                                    [key]: values
                                                }));
                                            }}
                                            clearable
                                            searchable
                                            comboboxProps={{ withinPortal: false }}
                                            styles={{
                                                input: {
                                                    borderRadius: '6px',
                                                    borderColor: '#e2e8f0',
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
